/**
 * Shopify Integration Setup Component
 * React component for setting up and managing Shopify integrations
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { ShopifyIntegrationFactory, ShopifyConnectionParams } from '../../services/integrations/ecommerce/ShopifyIntegrationFactory';
import { ShopifyIntegration } from '../../services/integrations/ecommerce/ShopifyIntegration';
import { TestResult } from '../../types/integration';
import { clickable } from "components/ui/interactive";

interface ShopifyIntegrationSetupProps {
  onIntegrationCreated?: (integration: ShopifyIntegration) => void;
  onCancel?: () => void;
  existingConfig?: ShopifyConnectionParams;
}

interface SetupStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
}

export const ShopifyIntegrationSetup: React.FC<ShopifyIntegrationSetupProps> = ({
  onIntegrationCreated,
  onCancel,
  existingConfig
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [config, setConfig] = useState<ShopifyConnectionParams>({
    shopDomain: existingConfig?.shopDomain || '',
    usePrivateApp: existingConfig?.usePrivateApp || false,
    accessToken: existingConfig?.accessToken || '',
    apiKey: existingConfig?.apiKey || '',
    apiSecret: existingConfig?.apiSecret || '',
    webhookSecret: existingConfig?.webhookSecret || '',
    scopes: existingConfig?.scopes || [
      'read_products',
      'write_products',
      'read_customers',
      'write_customers',
      'read_orders',
      'write_orders',
      'read_inventory',
      'write_inventory',
      'read_checkouts',
      'write_checkouts'
    ],
    apiVersion: existingConfig?.apiVersion || '2023-10'
  });
  
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [integration, setIntegration] = useState<ShopifyIntegration | null>(null);

  const steps: SetupStep[] = [
    {
      id: 'connection_type',
      title: 'Choose Connection Type',
      description: 'Select how you want to connect to Shopify',
      completed: false
    },
    {
      id: 'shop_details',
      title: 'Shop Details',
      description: 'Enter your Shopify store information',
      completed: false
    },
    {
      id: 'authentication',
      title: 'Authentication',
      description: 'Configure authentication credentials',
      completed: false
    },
    {
      id: 'permissions',
      title: 'Permissions',
      description: 'Select the data you want to sync',
      completed: false
    },
    {
      id: 'webhooks',
      title: 'Webhooks',
      description: 'Configure real-time updates',
      completed: false
    },
    {
      id: 'test_connection',
      title: 'Test Connection',
      description: 'Verify your Shopify connection',
      completed: false
    }
  ];

  const availableScopes = [
    { value: 'read_products', label: 'Read Products', description: 'View product information' },
    { value: 'write_products', label: 'Write Products', description: 'Modify product information' },
    { value: 'read_customers', label: 'Read Customers', description: 'View customer information' },
    { value: 'write_customers', label: 'Write Customers', description: 'Modify customer information' },
    { value: 'read_orders', label: 'Read Orders', description: 'View order information' },
    { value: 'write_orders', label: 'Write Orders', description: 'Modify order information' },
    { value: 'read_inventory', label: 'Read Inventory', description: 'View inventory levels' },
    { value: 'write_inventory', label: 'Write Inventory', description: 'Modify inventory levels' },
    { value: 'read_checkouts', label: 'Read Checkouts', description: 'View checkout information' },
    { value: 'write_checkouts', label: 'Write Checkouts', description: 'Modify checkout information' },
    { value: 'read_analytics', label: 'Read Analytics', description: 'View analytics data' },
    { value: 'read_marketing_events', label: 'Read Marketing Events', description: 'View marketing events' },
    { value: 'write_marketing_events', label: 'Write Marketing Events', description: 'Create marketing events' }
  ];

  useEffect(() => {
    validateCurrentStep();
  }, [config, currentStep]);

  const validateCurrentStep = () => {
    const errors: string[] = [];
    
    switch (steps[currentStep]?.id) {
      case 'shop_details':
        if (!config.shopDomain) {
          errors.push('Shop domain is required');
        } else if (!/^[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9]$/.test(config.shopDomain)) {
          errors.push('Invalid shop domain format');
        }
        break;
        
      case 'authentication':
        if (config.usePrivateApp) {
          if (!config.accessToken) {
            errors.push('Access token is required for private app');
          }
        } else {
          if (!config.apiKey) errors.push('API key is required');
          if (!config.apiSecret) errors.push('API secret is required');
        }
        break;
        
      case 'permissions':
        if (!config.scopes || config.scopes.length === 0) {
          errors.push('At least one permission scope is required');
        }
        break;
    }
    
    setValidationErrors(errors);
  };

  const handleNext = () => {
    if (validationErrors.length === 0 && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleTestConnection = async () => {
    setIsLoading(true);
    setTestResult(null);
    
    try {
      const validation = ShopifyIntegrationFactory.validateParams(config);
      if (!validation.valid) {
        setTestResult({
          success: false,
          message: 'Configuration validation failed',
          duration: 0
        });
        return;
      }

      const testIntegration = ShopifyIntegrationFactory.create(config);
      const result = await testIntegration.testConnection();
      setTestResult(result);
      
      if (result.success) {
        setIntegration(testIntegration);
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Connection test failed',
        duration: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = async () => {
    if (integration && testResult?.success) {
      try {
        setIsLoading(true);
        const connectionResult = await integration.connect();
        
        if (connectionResult.success) {
          onIntegrationCreated?.(integration);
        } else {
          setTestResult({
            success: false,
            message: 'Failed to establish connection',
            duration: 0
          });
        }
      } catch (error) {
        setTestResult({
          success: false,
          message: 'Connection failed',
          duration: 0
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleScopeToggle = (scope: string) => {
    const newScopes = config.scopes?.includes(scope)
      ? config.scopes.filter(s => s !== scope)
      : [...(config.scopes || []), scope];
    
    setConfig({ ...config, scopes: newScopes });
  };

  const renderStepContent = () => {
    switch (steps[currentStep]?.id) {
      case 'connection_type':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div 
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  config.usePrivateApp ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-line hover:border-line-strong'
                }`}
                {...clickable(() => setConfig({ ...config, usePrivateApp: true }))}
              >
                <h3 className="font-semibold text-lg mb-2">Private App</h3>
                <p className="text-content-secondary text-sm">
                  Use private app credentials for direct API access. Recommended for server-to-server integrations.
                </p>
                <ul className="mt-2 text-sm text-content-muted">
                  <li>• Simple setup with access token</li>
                  <li>• Full API access</li>
                  <li>• No user interaction required</li>
                </ul>
              </div>
              
              <div 
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  !config.usePrivateApp ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-line hover:border-line-strong'
                }`}
                {...clickable(() => setConfig({ ...config, usePrivateApp: false }))}
              >
                <h3 className="font-semibold text-lg mb-2">OAuth App</h3>
                <p className="text-content-secondary text-sm">
                  Use OAuth 2.0 for user-authorized access. Recommended for apps that need user consent.
                </p>
                <ul className="mt-2 text-sm text-content-muted">
                  <li>• User authorization required</li>
                  <li>• Granular permissions</li>
                  <li>• Token refresh support</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'shop_details':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Shop Domain *
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={config.shopDomain}
                  onChange={(e) => setConfig({ ...config, shopDomain: e.target.value })}
                  placeholder="your-store-name"
                  className="flex-1 px-3 py-2 border border-line-strong rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="px-3 py-2 bg-gray-100 dark:bg-dark-700 border border-l-0 border-line-strong rounded-r-md text-content-secondary">
                  .myshopify.com
                </span>
              </div>
              <p className="mt-1 text-sm text-content-muted">
                Enter your Shopify store domain without the .myshopify.com part
              </p>
            </div>
          </div>
        );

      case 'authentication':
        return (
          <div className="space-y-4">
            {config.usePrivateApp ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Access Token *
                </label>
                <input
                  type="password"
                  value={config.accessToken}
                  onChange={(e) => setConfig({ ...config, accessToken: e.target.value })}
                  placeholder="shpat_..."
                  className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-content-muted">
                  Get this from your Shopify admin under Apps → Private apps
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    API Key *
                  </label>
                  <input
                    type="text"
                    value={config.apiKey}
                    onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                    placeholder="Your app's API key"
                    className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    API Secret *
                  </label>
                  <input
                    type="password"
                    value={config.apiSecret}
                    onChange={(e) => setConfig({ ...config, apiSecret: e.target.value })}
                    placeholder="Your app's API secret"
                    className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}
          </div>
        );

      case 'permissions':
        return (
          <div className="space-y-4">
            <p className="text-sm text-content-secondary mb-4">
              Select the permissions your integration needs. Only request the permissions you actually use.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableScopes.map((scope) => (
                <div key={scope.value} className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id={scope.value}
                    checked={config.scopes?.includes(scope.value) || false}
                    onChange={() => handleScopeToggle(scope.value)}
                    className="mt-1 h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
                  />
                  <div className="flex-1">
                    <label htmlFor={scope.value} className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                      {scope.label}
                    </label>
                    <p className="text-xs text-content-muted">{scope.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'webhooks':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Webhook Secret (Optional)
              </label>
              <input
                type="password"
                value={config.webhookSecret}
                onChange={(e) => setConfig({ ...config, webhookSecret: e.target.value })}
                placeholder="Enter webhook secret for signature verification"
                className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-sm text-content-muted">
                Used to verify webhook authenticity. Recommended for production use.
              </p>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
              <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Webhook Events</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                The following webhook events will be automatically configured:
              </p>
              <ul className="text-sm text-blue-600 dark:text-blue-300 space-y-1">
                <li>• Order created, updated, paid, cancelled</li>
                <li>• Customer created, updated</li>
                <li>• Checkout created, updated (for cart abandonment)</li>
                <li>• Product created, updated</li>
                <li>• Inventory level updates</li>
              </ul>
            </div>
          </div>
        );

      case 'test_connection':
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-md">
              <h4 className="font-medium text-content mb-2">Configuration Summary</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-content-secondary">Shop Domain:</dt>
                  <dd className="font-medium">{config.shopDomain}.myshopify.com</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-content-secondary">Connection Type:</dt>
                  <dd className="font-medium">{config.usePrivateApp ? 'Private App' : 'OAuth App'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-content-secondary">API Version:</dt>
                  <dd className="font-medium">{config.apiVersion}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-content-secondary">Permissions:</dt>
                  <dd className="font-medium">{config.scopes?.length || 0} scopes</dd>
                </div>
              </dl>
            </div>
            
            {testResult && (
              <div className={`p-4 rounded-md ${testResult.success ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50'}`}>
                <div className="flex items-center">
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full ${testResult.success ? 'bg-green-500' : 'bg-red-500'}`}>
                    <span className="block w-full h-full text-white text-center text-xs leading-5">
                      {testResult.success ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="ml-3">
                    <h4 className={`font-medium ${testResult.success ? 'text-green-900 dark:text-green-300' : 'text-red-900 dark:text-red-300'}`}>
                      {testResult.message}
                    </h4>
                    {!testResult.success && (
                      <p className={`text-sm mt-1 ${testResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                        Connection failed
                      </p>
                    )}
                    {testResult.success && testResult.details && (
                      <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                        <p>Store: {testResult.details.shopName}</p>
                        <p>Email: {testResult.details.email}</p>
                        <p>Currency: {testResult.details.currency}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-content dark:text-gray-100 mb-2">
          {existingConfig ? 'Edit' : 'Setup'} Shopify Integration
        </h1>
        <p className="text-content-secondary dark:text-gray-400">
          Connect your Shopify store to sync customers, products, and orders with your email marketing platform.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                index <= currentStep ? 'border-blue-500 bg-blue-500 text-white' : 'border-line-strong text-content-subtle'
              }`}>
                {index < currentStep ? '✓' : index + 1}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-0.5 ${index < currentStep ? 'bg-blue-500' : 'bg-gray-300'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="mt-2">
          <h2 className="text-lg font-semibold text-content">{steps[currentStep]?.title}</h2>
          <p className="text-sm text-content-secondary">{steps[currentStep]?.description}</p>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-surface border border-line rounded-lg p-6 mb-6">
        {renderStepContent()}
        
        {validationErrors.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-md">
            <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <div>
          {currentStep > 0 && (
            <button
              onClick={handlePrevious}
              className="px-4 py-2 text-content-secondary hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              ← Previous
            </button>
          )}
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-content-secondary hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          
          {steps[currentStep]?.id === 'test_connection' ? (
            <div className="flex space-x-3">
              <button
                onClick={handleTestConnection}
                disabled={isLoading || validationErrors.length > 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Testing...' : 'Test Connection'}
              </button>
              
              {testResult?.success && (
                <button
                  onClick={handleFinish}
                  disabled={isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Connecting...' : 'Finish Setup'}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleNext}
              disabled={validationErrors.length > 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};